import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Eye, ChevronLeft, ChevronRight,
  Plus, FileText, Clock, CheckCircle, XCircle,
  Phone, Printer, RefreshCw, MapPin, Calendar,
  Filter, X, Loader2, Building2
} from 'lucide-react';
import { bookingsAPI, branchesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import toast from 'react-hot-toast';

const BookingsList = ({ fixedVisitType = '', pageTitle = 'قائمة الحجوزات', pageDescription = 'حجز' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canSwitchBranch } = useBranch();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [allBranches, setAllBranches] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 20;

  const userBranchId = user?.branch_id?._id || user?.branch_id || null;
  const isOwner = user?.role === 'owner';

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await branchesAPI.getAll({ active: true, limit: 100 });
        const data = response.data?.data?.items || [];
        setAllBranches(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading branches:', err);
      }
    };
    loadBranches();
  }, []);

  useEffect(() => {
    if (userBranchId && !isOwner && !canSwitchBranch) {
      setBranchFilter(userBranchId);
    } else if (branchFilter === '') {
      // For owner / multi-branch users, default to all branches to avoid accidental partial lists.
      setBranchFilter('all');
    }
  }, [userBranchId, isOwner, canSwitchBranch, branchFilter]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      let effectiveBranchId = branchFilter;
      if (userBranchId && !isOwner && !canSwitchBranch) {
        effectiveBranchId = userBranchId;
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(paymentFilter && { payment_status: paymentFilter }),
        ...(fixedVisitType && { visit_type: fixedVisitType }),
        ...(effectiveBranchId && effectiveBranchId !== 'all' && { branch_id: effectiveBranchId }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      };
      const response = await bookingsAPI.getAll(params);
      const data = response.data?.data;
      setBookings(data?.items || []);
      setTotalPages(data?.totalPages || Math.ceil((data?.total || 0) / itemsPerPage) || 1);
      setTotalItems(data?.total || data?.items?.length || 0);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('حدث خطأ أثناء جلب الحجوزات');
    }
    setLoading(false);
  }, [currentPage, searchQuery, statusFilter, paymentFilter, branchFilter, dateFrom, dateTo, userBranchId, isOwner, canSwitchBranch]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBookings();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) return;
    try {
      await bookingsAPI.cancel(id);
      toast.success('تم إلغاء الحجز بنجاح');
      fetchBookings();
    } catch (error) {
      toast.error('حدث خطأ أثناء إلغاء الحجز');
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPaymentFilter('');
    setDateFrom('');
    setDateTo('');
    if (isOwner || canSwitchBranch || !userBranchId) {
      setBranchFilter('all');
    }
    setCurrentPage(1);
  };

  const hasActiveFilters = statusFilter || paymentFilter || dateFrom || dateTo || (branchFilter && branchFilter !== 'all');

  const statusConfig = {
    online_unconfirmed: { label: 'عميل ويب غير مؤكد', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
    new: { label: 'جديد', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-500' },
    pending: { label: 'قيد الانتظار', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
    sample_collected: { label: 'تم التجميع', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
    collected: { label: 'تم التجميع', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
    in_progress: { label: 'قيد المعالجة', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500' },
    result_ready: { label: 'النتائج جاهزة', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    completed: { label: 'مكتمل', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    released: { label: 'تم الإصدار', bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500' },
    cancelled: { label: 'ملغي', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500' },
  };

  const paymentConfig = {
    paid: { label: 'مدفوع', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    partial: { label: 'جزئي', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    unpaid: { label: 'غير مدفوع', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
    refunded: { label: 'مرتجع', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.new;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide ${config.bg} ${config.text} border ${config.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
        {config.label}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const config = paymentConfig[status] || paymentConfig.unpaid;
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide ${config.bg} ${config.text} border ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{pageTitle}</h1>
            <p className="text-slate-500 text-sm mt-1">
              إجمالي <span className="font-semibold text-slate-700">{totalItems}</span> {pageDescription}
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/bookings/new')}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            <span>حجز جديد</span>
          </motion.button>
        </motion.div>

        {/* Search & Filters Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] mb-6"
        >
          {/* Main Search Bar */}
          <div className="p-4 border-b border-slate-100">
            <form onSubmit={handleSearch} className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
                <input
                  type="text"
                  placeholder="بحث برقم الحجز، الباركود، أو اسم العميل..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${showFilters || hasActiveFilters
                  ? 'bg-[#1E293B] text-white'
                  : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                  }`}
              >
                <Filter className="w-4 h-4 text-blue-600" strokeWidth={2} />
                <span className="hidden sm:inline">فلترة</span>
                {hasActiveFilters && (
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                    {[statusFilter, paymentFilter, dateFrom, dateTo].filter(Boolean).length}
                  </span>
                )}
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all duration-200"
              >
                بحث
              </button>
            </form>
          </div>

          {/* Expandable Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-slate-50/50 border-t border-slate-100">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {/* Status Filter */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all appearance-none cursor-pointer"
                      >
                        <option value="">الكل</option>
                        <option value="online_unconfirmed">عميل ويب غير مؤكد</option>
                        <option value="new">جديد</option>
                        <option value="sample_collected">تم التجميع</option>
                        <option value="in_progress">قيد المعالجة</option>
                        <option value="completed">مكتمل</option>
                        <option value="cancelled">ملغي</option>
                      </select>
                    </div>

                    {/* Payment Filter */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الدفع</label>
                      <select
                        value={paymentFilter}
                        onChange={(e) => { setPaymentFilter(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all appearance-none cursor-pointer"
                      >
                        <option value="">الكل</option>
                        <option value="paid">مدفوع</option>
                        <option value="partial">جزئي</option>
                        <option value="unpaid">غير مدفوع</option>
                        <option value="refunded">مرتجع</option>
                      </select>
                    </div>

                    {/* Branch Filter */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الفرع</label>
                      <select
                        value={branchFilter}
                        onChange={(e) => { setBranchFilter(e.target.value); setCurrentPage(1); }}
                        disabled={userBranchId && !isOwner && !canSwitchBranch}
                        className={`w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all appearance-none cursor-pointer ${userBranchId && !isOwner && !canSwitchBranch ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                      >
                        {(isOwner || canSwitchBranch || !userBranchId) && (
                          <option value="all">جميع الفروع</option>
                        )}
                        {allBranches.map(branch => (
                          <option key={branch._id} value={branch._id}>{branch.branch_name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Date From */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">من تاريخ</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
                      />
                    </div>

                    {/* Date To */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">إلى تاريخ</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
                      />
                    </div>
                  </div>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <div className="mt-3 pt-3 border-t border-slate-200/60">
                      <button
                        onClick={clearFilters}
                        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                        مسح الفلاتر
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Table Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-3" />
              <p className="text-sm text-slate-500">جاري تحميل الحجوزات...</p>
            </div>
          ) : bookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">لا توجد حجوزات</h3>
              <p className="text-sm text-slate-500 mb-4">ابدأ بإنشاء حجز جديد</p>
              <button
                onClick={() => navigate('/bookings/new')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                إنشاء حجز جديد
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">رقم الحجز</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">العميل</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الفرع</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">التاريخ</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">المبلغ</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الدفع</th>
                      <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking, index) => (
                      <motion.tr
                        key={booking._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                      >
                        {/* Booking ID */}
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-slate-900 text-sm tracking-tight">
                              #{booking.booking_no}
                            </span>
                            {booking.visit_type === 'contractor' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                                <Building2 className="w-3 h-3" />
                                متعاقد
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Customer */}
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-bold text-slate-600">
                                {booking.customer_id?.full_name?.charAt(0) || '؟'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900 text-sm truncate">
                                {booking.customer_id?.full_name || '-'}
                              </p>
                              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                <Phone className="w-3 h-3" strokeWidth={2} />
                                <span dir="ltr">{booking.customer_id?.phone || '-'}</span>
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Branch */}
                        <td className="px-6 py-5">
                          {booking.visit_type === 'contractor' ? (
                            <div className="flex items-center gap-1.5 text-purple-600">
                              <Building2 className="w-3.5 h-3.5" strokeWidth={2} />
                              <span className="text-sm font-medium">{booking.contractor_lab_id?.name || 'مختبر متعاقد'}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-slate-600">
                              <MapPin className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                              <span className="text-sm">{booking.branch_id?.branch_name || '-'}</span>
                            </div>
                          )}
                        </td>

                        {/* Date */}
                        <td className="px-6 py-5">
                          <div className="text-sm">
                            <p className="font-medium text-slate-900">{formatDate(booking.booking_date)}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{formatTime(booking.booking_date)}</p>
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="px-6 py-5">
                          <div className="text-sm">
                            <span className="font-bold text-slate-900 tabular-nums">
                              {booking.net_amount?.toFixed(2)}
                            </span>
                            <span className="text-slate-500 text-xs mr-1">ر.س</span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-5">
                          {getStatusBadge(booking.status)}
                        </td>

                        {/* Payment */}
                        <td className="px-6 py-5">
                          {getPaymentBadge(booking.payment_status)}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => navigate(`/bookings/${booking._id}`)}
                              className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                              title="عرض التفاصيل"
                            >
                              <Eye className="w-4 h-4" strokeWidth={2} />
                            </button>
                            {/* Contractor bookings are settled via account statement — no ZATCA invoice */}
                            {booking?.visit_type !== 'contractor' && (
                              <button
                                onClick={() => {
                                  if (booking?.payment_status !== 'paid') {
                                    toast.error('لا يمكن طباعة الفاتورة قبل تأكيد الدفع');
                                    return;
                                  }
                                  navigate(`/bookings/${booking._id}/invoice`);
                                }}
                                className={`p-2 rounded-lg transition-all duration-150 ${booking?.payment_status === 'paid'
                                  ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                                  : 'text-slate-300 cursor-not-allowed'
                                  }`}
                                title={booking?.payment_status === 'paid' ? 'طباعة الفاتورة' : 'يلزم تأكيد الدفع أولاً'}
                                disabled={booking?.payment_status !== 'paid'}
                              >
                                <Printer className="w-4 h-4" strokeWidth={2} />
                              </button>
                            )}
                            {booking.status !== 'cancelled' && (
                              <button
                                onClick={() => handleDelete(booking._id)}
                                className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                                title="إلغاء الحجز"
                              >
                                <XCircle className="w-4 h-4" strokeWidth={2} />
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden divide-y divide-slate-100">
                {bookings.map((booking, index) => (
                  <motion.div
                    key={booking._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 hover:bg-slate-50/50 transition-colors"
                  >
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-slate-600">
                            {booking.customer_id?.full_name?.charAt(0) || '؟'}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">
                            {booking.customer_id?.full_name || '-'}
                          </p>
                          <p className="text-xs text-slate-500 font-mono">#{booking.booking_no}</p>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-slate-900 text-sm tabular-nums">
                          {booking.net_amount?.toFixed(2)} <span className="text-xs text-slate-500">ر.س</span>
                        </p>
                      </div>
                    </div>

                    {/* Info Row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(booking.booking_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTime(booking.booking_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {booking.branch_id?.branch_name || '-'}
                      </span>
                    </div>

                    {/* Badges & Actions Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(booking.status)}
                        {getPaymentBadge(booking.payment_status)}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/bookings/${booking._id}`)}
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
                        >
                          <Eye className="w-4 h-4" strokeWidth={2} />
                        </button>
                        {booking?.visit_type !== 'contractor' && (
                          <button
                            onClick={() => {
                              if (booking?.payment_status !== 'paid') {
                                toast.error('لا يمكن طباعة الفاتورة قبل تأكيد الدفع');
                                return;
                              }
                              navigate(`/bookings/${booking._id}/invoice`);
                            }}
                            className={`p-2 rounded-lg transition-all ${booking?.payment_status === 'paid'
                              ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                              : 'text-slate-300 cursor-not-allowed'
                              }`}
                            disabled={booking?.payment_status !== 'paid'}
                          >
                            <Printer className="w-4 h-4" strokeWidth={2} />
                          </button>
                        )}
                        {booking.status !== 'cancelled' && (
                          <button
                            onClick={() => handleDelete(booking._id)}
                            className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                          >
                            <XCircle className="w-4 h-4" strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              <div className="px-4 sm:px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/30">
                <p className="text-sm text-slate-600 order-2 sm:order-1">
                  عرض <span className="font-semibold text-slate-900">{((currentPage - 1) * itemsPerPage) + 1}</span> - <span className="font-semibold text-slate-900">{Math.min(currentPage * itemsPerPage, totalItems)}</span> من <span className="font-semibold text-slate-900">{totalItems}</span>
                </p>
                <div className="flex items-center gap-2 order-1 sm:order-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight className="w-4 h-4 text-slate-600" strokeWidth={2} />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${currentPage === pageNum
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="w-4 h-4 text-slate-600" strokeWidth={2} />
                  </button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default BookingsList;
